import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getDriver } from 'src/selectors/driver'
import { getIsPartnerDeactivated, getIsPartnerFetchPending } from 'src/selectors/partner'
import { Typography } from '@mui/material'
import tw from 'twin.macro'
import { LoadingPage } from '../LoadingPage'
import { useNavigate } from 'react-router-dom'
import { CONTRACTOR_PORTAL_ROUTES } from 'src/constants/routes'
import { ContractorPortalSecureWrapper } from 'src/components/layout/contractorPortal/ContractorPortalSecureWrapper'
import {
  BalanceMajor,
  ChecklistAlternateMajor,
  ChevronRightMinor,
  CustomersMajor,
  JobsMajor,
  ReceiptMajor,
} from '@shopify/polaris-icons'
import {
  getContractorPortalCertificatesCount,
  getContractorPortalDocumentsCount,
  getContractorPortalInvoicesCount,
} from 'src/selectors/contractorPortal'
import Background from 'src/assets/images/background-pattern.svg'
import { GigSafeMemberBenefitPortal } from 'src/components/shared/GigSafeMemberBenefitPortal'
import { Footer } from 'src/components/shared/Footer'
import { CollectDataStepType, CollectDataStepStatus } from 'src/constants/collect-data/enums'
import { isDeprecatedStitchPaymentStepType } from 'src/utils/collectData'

const ContractorPortalHomePageComponent = () => {
  const navigate = useNavigate()

  const driver = useSelector(getDriver)
  const isPartnerDeactivated = useSelector(getIsPartnerDeactivated)
  const isPartnerFetchPending = useSelector(getIsPartnerFetchPending)
  const invoicesCount = useSelector(getContractorPortalInvoicesCount)
  const documentsCount = useSelector(getContractorPortalDocumentsCount)
  const certificatesCount = useSelector(getContractorPortalCertificatesCount)

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  useEffect(() => {
    if (driver && !isPartnerFetchPending) {
      setHasLoadedOnce(true)
    }
  }, [driver, isPartnerFetchPending])

  const handleOpenDocuments = useCallback(() => {
    navigate(CONTRACTOR_PORTAL_ROUTES.DOCUMENT_URL)
  }, [])

  const handleOpenCertificates = useCallback(() => {
    navigate(CONTRACTOR_PORTAL_ROUTES.CERTIFICATES_URL)
  }, [])

  const handleOpenInvoices = useCallback(() => {
    navigate(CONTRACTOR_PORTAL_ROUTES.INVOICES_URL)
  }, [])

  const handleOpenPayment = useCallback(() => {
    navigate(CONTRACTOR_PORTAL_ROUTES.PAYMENT_URL)
  }, [])

  const handleOpenContactDetails = useCallback(() => {
    navigate(CONTRACTOR_PORTAL_ROUTES.CONTACT_DETAILS_URL)
  }, [])

  const hasCompletedPaymentStep = useMemo(() => {
    if (isPartnerDeactivated || !driver?.collectDataStepsConfigs) {
      return false
    }

    return driver.collectDataStepsConfigs.some((config) => {
      return config.collectDataSteps.some((step) => {
        const isPaymentStep =
          step.type === CollectDataStepType.PaymentMethod ||
          isDeprecatedStitchPaymentStepType(step.type)

        return isPaymentStep && step.status === CollectDataStepStatus.Completed
      })
    })
  }, [isPartnerDeactivated, driver?.collectDataStepsConfigs])

  if (!driver || (!hasLoadedOnce && isPartnerFetchPending)) {
    return <LoadingPage flow="contractorPortal" />
  }

  return (
    <ContractorPortalSecureWrapper>
      <div css={tw`relative w-full`}>
        <div css={tw`w-full h-[240px] sm:h-[180px] bg-primary-500 relative`}>
          <img
            src={Background}
            css={tw`absolute w-full h-full top-0 left-0 opacity-20 object-cover`}
          />
        </div>
        <div css={tw`relative -mt-[160px] sm:-mt-[120px] p-4 py-8 pt-4 flex flex-col`}>
          <Typography variant="overline" css={tw`text-shades-0`}>
            WELCOME BACK
          </Typography>
          <Typography css={tw`mb-4 mt-0.5 text-shades-0`} variant="h1">
            {`${driver?.firstName} ${driver?.lastName}`}
          </Typography>
          <div>
            <div css={tw`rounded-2xl overflow-hidden bg-shades-0`}>
              <div
                css={tw`p-4 flex cursor-pointer items-center bg-shades-0 justify-between hover:bg-shades-100 active:bg-shades-200`}
                onClick={handleOpenInvoices}
                data-testid="contractors-list-invoices"
              >
                <div css={tw`flex items-center`}>
                  <div
                    css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50 mr-4`}
                  >
                    <ReceiptMajor width={20} height={20} />
                  </div>
                  <div>
                    <Typography variant="h4" css={tw`mb-0.5`}>
                      Invoices
                    </Typography>
                    <Typography css={tw`mb-0.5 text-shades-400`}>
                      {`${invoicesCount} invoices`}
                    </Typography>
                  </div>
                </div>
                <ChevronRightMinor fill="black" width={20} height={20} />
              </div>
              <div css={tw`h-[1px] bg-shades-100 mx-4`} />
              <div
                css={tw`p-4 flex cursor-pointer items-center bg-shades-0 justify-between hover:bg-shades-100 active:bg-shades-200`}
                onClick={handleOpenDocuments}
                data-testid="contractors-list-documents"
              >
                <div css={tw`flex items-center`}>
                  <div
                    css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50 mr-4`}
                  >
                    <JobsMajor width={20} height={20} />
                  </div>
                  <div>
                    <Typography variant="h4" css={tw`mb-0.5`}>
                      Documents
                    </Typography>
                    <Typography css={tw`mb-0.5 text-shades-400`}>
                      {`${documentsCount} documents`}
                    </Typography>
                  </div>
                </div>
                <ChevronRightMinor fill="black" width={20} height={20} />
              </div>
              <div css={tw`h-[1px] bg-shades-100 mx-4`} />
              <div
                css={tw`p-4 flex cursor-pointer items-center bg-shades-0 justify-between hover:bg-shades-100 active:bg-shades-200`}
                onClick={handleOpenCertificates}
                data-testid="contractors-list-certificates"
              >
                <div css={tw`flex items-center`}>
                  <div
                    css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50 mr-4`}
                  >
                    <ChecklistAlternateMajor width={20} height={20} />
                  </div>
                  <div>
                    <Typography variant="h4" css={tw`mb-0.5`}>
                      Certificates
                    </Typography>
                    <Typography css={tw`mb-0.5 text-shades-400`}>
                      {`${certificatesCount} certificate${certificatesCount === 1 ? '' : 's'}`}
                    </Typography>
                  </div>
                </div>
                <ChevronRightMinor fill="black" width={20} height={20} />
              </div>
              {(hasCompletedPaymentStep || !isPartnerDeactivated) && (
                <div css={tw`h-[1px] bg-shades-100 mx-4`} />
              )}
              {hasCompletedPaymentStep && (
                <Fragment>
                  <div
                    css={tw`p-4 flex cursor-pointer items-center bg-shades-0 justify-between hover:bg-shades-100 active:bg-shades-200`}
                    onClick={handleOpenPayment}
                    data-testid="contractors-list-payment-method"
                  >
                    <div css={tw`flex items-center`}>
                      <div
                        css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50 mr-4`}
                      >
                        <BalanceMajor width={20} height={20} />
                      </div>
                      <div>
                        <Typography variant="h4" css={tw`mb-0.5`}>
                          Settlement Method
                        </Typography>
                        <Typography css={tw`mb-0.5 text-shades-400`}>View and change</Typography>
                      </div>
                    </div>
                    <ChevronRightMinor fill="black" width={20} height={20} />
                  </div>
                  <div css={tw`h-[1px] bg-shades-100 mx-4`} />
                </Fragment>
              )}
              {!isPartnerDeactivated && (
                <div
                  css={tw`p-4 flex cursor-pointer items-center bg-shades-0 justify-between hover:bg-shades-100 active:bg-shades-200`}
                  onClick={handleOpenContactDetails}
                  data-testid="contractors-list-contact-details"
                >
                  <div css={tw`flex items-center`}>
                    <div
                      css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50 mr-4`}
                    >
                      <CustomersMajor width={20} height={20} />
                    </div>
                    <div>
                      <Typography variant="h4" css={tw`mb-0.5`}>
                        Contact Details
                      </Typography>
                      <Typography css={tw`mb-0.5 text-shades-400`}>
                        Email and phone number
                      </Typography>
                    </div>
                  </div>
                  <ChevronRightMinor fill="black" width={20} height={20} />
                </div>
              )}
            </div>
          </div>
          {isPartnerDeactivated ? (
            <div css={tw`mt-4`} />
          ) : (
            <div css={tw`my-4 mb-6`}>
              <GigSafeMemberBenefitPortal />
            </div>
          )}
          <Footer className={tw`w-full mt-auto`} />
        </div>
      </div>
    </ContractorPortalSecureWrapper>
  )
}

export const ContractorPortalHomePage = memo(ContractorPortalHomePageComponent)
